/**
 * Single source of truth for the transition catalogue on the web side —
 * consumed by the editor selects (TransitionEditorPopover) and by both
 * renderers (Player + PreviewDialog via transitionEngine).
 *
 * Ids are the stable, platform-independent strings shared verbatim with
 * the Laravel backend (app/Support/Transitions.php) and the Android app
 * (playback/Transitions.kt). Never store the label — only the id.
 */

export type TransitionId = string;
export type TransitionEasing =
  | "linear" | "ease" | "ease_in" | "ease_out" | "ease_in_out";

export interface TransitionOption {
  id: TransitionId;
  label: string;
  group: "Basic" | "Directional" | "Zoom" | "Advanced";
  /** Safe for videos (no re-layout / clip that fights the video surface). */
  videoSafe: boolean;
}

export const TRANSITIONS: TransitionOption[] = [
  // Basic
  { id: "none", label: "None", group: "Basic", videoSafe: true },
  { id: "cut", label: "Cut", group: "Basic", videoSafe: true },
  { id: "fade", label: "Fade", group: "Basic", videoSafe: true },
  { id: "crossfade", label: "Crossfade", group: "Basic", videoSafe: true },
  { id: "dissolve", label: "Dissolve", group: "Basic", videoSafe: true },
  // Directional
  { id: "slide_left", label: "Slide Left", group: "Directional", videoSafe: true },
  { id: "slide_right", label: "Slide Right", group: "Directional", videoSafe: true },
  { id: "slide_up", label: "Slide Up", group: "Directional", videoSafe: true },
  { id: "slide_down", label: "Slide Down", group: "Directional", videoSafe: true },
  { id: "push_left", label: "Push Left", group: "Directional", videoSafe: true },
  { id: "push_right", label: "Push Right", group: "Directional", videoSafe: true },
  { id: "push_up", label: "Push Up", group: "Directional", videoSafe: true },
  { id: "push_down", label: "Push Down", group: "Directional", videoSafe: true },
  { id: "wipe_left", label: "Wipe Left", group: "Directional", videoSafe: false },
  { id: "wipe_right", label: "Wipe Right", group: "Directional", videoSafe: false },
  { id: "wipe_up", label: "Wipe Up", group: "Directional", videoSafe: false },
  { id: "wipe_down", label: "Wipe Down", group: "Directional", videoSafe: false },
  { id: "reveal", label: "Reveal", group: "Directional", videoSafe: false },
  // Zoom
  { id: "zoom_in", label: "Zoom In", group: "Zoom", videoSafe: false },
  { id: "zoom_out", label: "Zoom Out", group: "Zoom", videoSafe: false },
  { id: "zoom_fade", label: "Zoom Fade", group: "Zoom", videoSafe: true },
  { id: "scale_fade", label: "Scale Fade", group: "Zoom", videoSafe: true },
  // Advanced
  { id: "blur_fade", label: "Blur Fade", group: "Advanced", videoSafe: true },
  { id: "flip_horizontal", label: "Flip Horizontal", group: "Advanced", videoSafe: false },
  { id: "flip_vertical", label: "Flip Vertical", group: "Advanced", videoSafe: false },
  { id: "rotate_fade", label: "Rotate Fade", group: "Advanced", videoSafe: false },
  { id: "ken_burns", label: "Ken Burns", group: "Advanced", videoSafe: false },
  { id: "pan_left", label: "Pan Left", group: "Advanced", videoSafe: false },
  { id: "pan_right", label: "Pan Right", group: "Advanced", videoSafe: false },
  { id: "pan_up", label: "Pan Up", group: "Advanced", videoSafe: false },
  { id: "pan_down", label: "Pan Down", group: "Advanced", videoSafe: false },
];

/** Subset offered for playlist-to-playlist (group) boundaries. */
export const GROUP_TRANSITION_IDS: TransitionId[] = [
  "none", "cut", "fade", "crossfade", "dissolve",
  "slide_left", "slide_right", "slide_up", "slide_down",
  "push_left", "push_right", "wipe_left", "wipe_right",
  "zoom_fade", "blur_fade", "scale_fade",
];

export const GROUP_TRANSITIONS: TransitionOption[] =
  TRANSITIONS.filter(t => GROUP_TRANSITION_IDS.includes(t.id));

export const EASINGS: { id: TransitionEasing; label: string }[] = [
  { id: "linear", label: "Linear" },
  { id: "ease", label: "Ease" },
  { id: "ease_in", label: "Ease In" },
  { id: "ease_out", label: "Ease Out" },
  { id: "ease_in_out", label: "Ease In Out" },
];

/** Standard duration presets (seconds) offered in the editor. */
export const DURATION_PRESETS_SECONDS = [0.2, 0.3, 0.5, 0.75, 1, 1.5, 2, 3];

export const MIN_DURATION_MS = 100;
export const MAX_DURATION_MS = 5000;
export const DEFAULT_DURATION_MS = 500;
export const DEFAULT_EASING: TransitionEasing = "ease_in_out";
export const DEFAULT_TRANSITION_ID = "cut";

export const DURATION_RANGE_MESSAGE =
  "Transition duration must be between 0.1 and 5 seconds.";

const IDS = new Set(TRANSITIONS.map(t => t.id));
const EASE_IDS = new Set(EASINGS.map(e => e.id));

export function isValidTransitionId(id: string | null | undefined): boolean {
  return !!id && IDS.has(id);
}

export function isValidEasing(id: string | null | undefined): id is TransitionEasing {
  return !!id && EASE_IDS.has(id as TransitionEasing);
}

export function transitionLabel(id: string | null | undefined): string {
  return TRANSITIONS.find(t => t.id === id)?.label ?? "Cut";
}

export function isVideoSafe(id: string | null | undefined): boolean {
  return TRANSITIONS.find(t => t.id === id)?.videoSafe ?? true;
}

export function clampDurationMs(ms: number | null | undefined): number {
  const v = ms ?? DEFAULT_DURATION_MS;
  return Math.max(MIN_DURATION_MS, Math.min(MAX_DURATION_MS, Math.round(v)));
}
